import React, { useCallback, useEffect, useState } from 'react';
import AdminLayout from '../../layout/AdminLayout';
import { useAuthContext } from '../../../context/AuthContext';
import { HTTPResponse } from '@/class/HTTPResponse';
import { Store } from '@/class/Store';
import { fetchData, putData } from '../../../lib/api';
import StoreEditModal from './editModal';
import { parseStoreForm, storeToForm, StoreForm } from './storeForm';
import { adminCard, adminSectionTitle, adminMutedText, adminButtonPrimary, adminPageToolbar, adminSectionTitleToolbar } from '../../layout/adminStyles';

const formatTime = (value: string | undefined | null): string => {
  if (!value) {
    return '—';
  }

  const match = value.match(/(\d{1,2}):(\d{2})/);
  if (!match) {
    return value;
  }

  return `${match[1]}:${match[2]}`;
};

const displayValue = (value?: string | number | null): string => {
  if (value === null || value === undefined || value === '') {
    return '—';
  }

  return String(value);
};

const formatBoolean = (value?: boolean | null): string => {
  if (value === null || value === undefined) {
    return '—';
  }

  return value ? 'あり' : 'なし';
};

const formatCoordinate = (value?: number | null): string => {
  if (value === null || value === undefined) {
    return '—';
  }

  return value.toFixed(6);
};

type StoreField = {
  label: string;
  render: (store: Store) => React.ReactNode;
};

const STORE_FIELDS: StoreField[] = [
  { label: 'ID', render: (store) => displayValue(store.id) },
  { label: '店舗名', render: (store) => displayValue(store.name) },
  { label: '説明', render: (store) => displayValue(store.description) },
  { label: '郵便番号', render: (store) => displayValue(store.zip) },
  { label: '都道府県', render: (store) => displayValue(store.pref) },
  { label: '市区町村', render: (store) => displayValue(store.city) },
  { label: '住所', render: (store) => displayValue(store.address) },
  { label: '建物名', render: (store) => displayValue(store.building) },
  { label: '緯度', render: (store) => formatCoordinate(store.latitude) },
  { label: '経度', render: (store) => formatCoordinate(store.longitude) },
  { label: '電話番号', render: (store) => displayValue(store.phone_number) },
  { label: 'メールアドレス', render: (store) => displayValue(store.email) },
  { label: 'ログインID', render: (store) => displayValue(store.login_id) },
  {
    label: '営業時間',
    render: (store) => `${formatTime(store.open_time)}〜${formatTime(store.close_time)}`,
  },
  { label: '駐車場', render: (store) => formatBoolean(store.has_parking) },
  {
    label: 'タイムセール告知',
    render: (store) => formatBoolean(store.is_time_sale_announced ?? false),
  },
  { label: '天気コード', render: (store) => displayValue(store.weather_code) }  
];

const AdminStoreShowPage = () => {
  const { store, updateStore } = useAuthContext();
  const [displayStore, setDisplayStore] = useState<Store | null>(store?.id ? store : null);
  const [isLoading, setIsLoading] = useState<boolean>(Boolean(store?.id));
  const [isShowModal, setIsShowModal] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [form, setForm] = useState<StoreForm>(storeToForm(store));

  const labelClass = 'block text-stone-700 font-medium mb-1 text-sm';
  const valueClass = `block ${adminMutedText}`;

  const storeId = store?.id;

  const loadStore = useCallback(async () => {
    if (!storeId) {
      setDisplayStore(null);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    try {
      const res: HTTPResponse = await fetchData(`store/${storeId}`);
      if (res.data) {
        setDisplayStore(res.data as Store);
      }
    } catch (error: unknown) {
      console.error(error);
      setDisplayStore(store?.id ? store : null);
    } finally {
      setIsLoading(false);
    }
  }, [storeId, store]);

  useEffect(() => {
    loadStore();
  }, [loadStore]);

  const openModal = (): void => {
    setForm(storeToForm(displayStore ?? store));
    setIsShowModal(true);
  };

  const closeModal = (): void => {
    if (!isSaving) {
      setIsShowModal(false);
    }
  };

  const handleSubmit = async (): Promise<void> => {
    const payload = parseStoreForm(form);
    if (!payload || !store?.id) {
      return;
    }

    setIsSaving(true);
    try {
      const res: HTTPResponse = await putData(`store/${store.id}`, payload);
      if (res.data) {
        const updated = res.data as Store;
        updateStore(updated);
        setDisplayStore(updated);
        setIsShowModal(false);
        alert('店舗情報を更新しました。');
      }
    } catch (error: unknown) {
      console.error(error);
      const message = error instanceof Error ? error.message : '店舗情報の更新に失敗しました。';
      alert(message.includes('ログイン') ? message : '店舗情報の更新に失敗しました。');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <AdminLayout>
      <div className={adminCard}>
        <div className={adminPageToolbar}>
          <h3 className={adminSectionTitleToolbar}>店舗情報詳細</h3>
          <button type="button" className={`${adminButtonPrimary} w-full sm:w-auto`} onClick={openModal}>
            編集
          </button>
        </div>

        {isLoading ? (
          <p className={adminMutedText}>読み込み中...</p>
        ) : !displayStore ? (
          <p className={adminMutedText}>店舗情報を取得できませんでした。</p>
        ) : (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            {STORE_FIELDS.map((field) => (
              <div key={field.label} className="mb-1">
                <strong className={labelClass}>{field.label}</strong>
                <span className={valueClass}>{field.render(displayStore)}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      <StoreEditModal
        isShowModal={isShowModal}
        form={form}
        isSaving={isSaving}
        onChange={setForm}
        onClose={closeModal}
        onSubmit={handleSubmit}
      />
    </AdminLayout>
  );
};

export default AdminStoreShowPage;
